import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { AsyncValidatorFn, FormControl, NgControl, ValidationErrors } from '@angular/forms';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker/src/app/material-timepicker/models/ngx-material-timepicker-theme.interface';
import { interval, Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { SimpleField } from '../../abstracts/simple-field';
import { TimeFieldConfig } from '../../models/fields/time-field-config.model';

@Component({
  selector: 'fb-timepicker-field',
  templateUrl: './timepicker-field.component.html',
  styleUrls: ['./timepicker-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatFormFieldControl, useExisting: TimepickerFieldComponent }],
})
export class TimepickerFieldComponent
  extends SimpleField<string>
  implements OnInit, OnDestroy, MatFormFieldControl<string>
{
  @Input()
  override get required(): boolean {
    return this.isRequired;
  }
  override set required(req: boolean) {
    this.isRequired = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  get empty(): boolean {
    return !this.value;
  }

  get errorState(): boolean {
    return this.timepickerControl.invalid && this.timepickerControl.touched;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @HostBinding('id')
  get elementId(): string {
    return this.id;
  }

  set disabled(value: boolean) {
    this.disable = Boolean(value);
  }
  get disabled(): boolean {
    return this.disable;
  }

  /* eslint-disable-next-line @angular-eslint/no-input-rename */
  @Input('aria-describedby') userAriaDescribedBy: string;

  @Input() config: TimeFieldConfig;
  @Input() min: number;
  @Input() max: number;
  @Input() format: number | string = 24;

  @ViewChild('zuumTimePicker') zuumTimePickerElement: HTMLElement;

  static nextId: number = 0;

  id: string = `example-tel-input-${TimepickerFieldComponent.nextId++}`;

  controlType: string = 'timepicker';
  focused: boolean = false;
  timepickerControl: FormControl = new FormControl(undefined, { asyncValidators: [this.validateControl(this)] });
  clockTheme: NgxMaterialTimepickerTheme = {
    container: {
      buttonColor: '#204fc6',
    },
    dial: {
      dialBackgroundColor: '#204fc6',
    },
    clockFace: {
      clockHandColor: '#204fc6',
    },
  };

  private isRequired: boolean = false;

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional() @Self() public override ngControl: NgControl,
    private focusMonitor: FocusMonitor,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public formField: MatFormField,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.initListeners();
  }

  override ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  onContainerClick(): void {
    this.focusMonitor.focusVia(this.zuumTimePickerElement, 'program');
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement: HTMLElement = this.elementRef.nativeElement.querySelector('.timepicker-container');
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(): void {
    this.timepickerControl.markAsTouched();
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  protected override onWriteValue(value: string): void {
    if (!(value === undefined || value === null || value === '')) {
      const [h, minutes]: string[] = this.value.split(':');
      let hours: string = h;
      if (hours.length === 1) {
        hours = `0${hours}`;
      }

      this.timepickerControl.setValue(`${hours}:${minutes}`, { emitEvent: false });
    } else {
      this.timepickerControl.reset();
      this.timepickerControl.setErrors(null);
    }
  }

  private validateControl(component: TimepickerFieldComponent): AsyncValidatorFn {
    return (): Observable<ValidationErrors | null> => {
      return interval(1).pipe(
        take(1),
        map(() =>
          !component.ngControl?.control || component.ngControl.control.valid
            ? null
            : component.ngControl.control.errors,
        ),
      );
    };
  }

  private initListeners(): void {
    this.timepickerControl.valueChanges
      .pipe(untilComponentDestroyed(this), distinctUntilChanged())
      .subscribe((value: string) => {
        this.value = value;
      });
  }
}
